import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Save, RefreshCw, Trash2, Plus } from "lucide-react";

const API = "/api";

export default function SettingsPage() {
  const { token } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("connection");

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings?token=${token}`);
      setSettings(response.data);
    } catch (error) {
      toast.error("Ayarlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (type, data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/${type}?token=${token}`, data);
      toast.success("Ayarlar kaydedildi");
    } catch (error) {
      toast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Ayarlar</h1>
        <p className="text-slate-500 mt-1">Entegrasyon ayarlarını yapılandırın</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1 bg-slate-100">
          <TabsTrigger value="connection" data-testid="tab-connection">Bağlantı Ayarları</TabsTrigger>
          <TabsTrigger value="product-mapping" data-testid="tab-product-mapping">Ürün Eşleştirme</TabsTrigger>
          <TabsTrigger value="order" data-testid="tab-order">Sipariş Ayarları</TabsTrigger>
          {/* <TabsTrigger value="update" data-testid="tab-update">Güncelleme Ayarları</TabsTrigger> */}
          {/* <TabsTrigger value="auto-transfer" data-testid="tab-auto-transfer">Otomatik Güncelleme</TabsTrigger> */}
          <TabsTrigger value="payment-codes" data-testid="tab-payment-codes">Ödeme Kodları</TabsTrigger>
          <TabsTrigger value="earchive" data-testid="tab-earchive">E-Arşiv</TabsTrigger>
          <TabsTrigger value="bundle-transfer" data-testid="tab-bundle-transfer">Asorti Aktarım</TabsTrigger>
        </TabsList>

        {/* Connection Settings */}
        <TabsContent value="connection">
          <ConnectionSettings settings={settings?.connection} token={token} onSave={(data) => saveSettings("connection", data)} saving={saving} />
        </TabsContent>

        {/* Product Mapping */}
        <TabsContent value="product-mapping">
          <ProductMappingSettings settings={settings?.product_mapping} token={token} onSave={(data) => saveSettings("product-mapping", data)} saving={saving} />
        </TabsContent>

        {/* Order Settings */}
        <TabsContent value="order">
          <OrderSettings settings={settings?.order} token={token} onSave={(data) => saveSettings("order", data)} saving={saving} />
        </TabsContent>

        {/* Update Settings */}
        <TabsContent value="update">
          <UpdateSettings settings={settings?.update} token={token} onSave={(data) => saveSettings("update", data)} saving={saving} />
        </TabsContent>

        {/* Auto Transfer Settings */}
        <TabsContent value="auto-transfer">
          <AutoTransferSettings settings={settings?.auto_transfer} token={token} onSave={(data) => saveSettings("auto-transfer", data)} saving={saving} />
        </TabsContent>

        {/* Payment Codes */}
        <TabsContent value="payment-codes">
          <PaymentCodesSettings token={token} />
        </TabsContent>

        {/* E-Archive */}
        <TabsContent value="earchive">
          <EArchiveSettings token={token} />
        </TabsContent>

        {/* Bundle Transfer Settings */}
        <TabsContent value="bundle-transfer">
          <BundleTransferSettings settings={settings?.bundle_transfer} token={token} onSave={(data) => saveSettings("bundle-transfer", data)} saving={saving} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Connection Settings Component
function ConnectionSettings({ settings, onSave, saving }) {
  const [data, setData] = useState(settings || {});

  useEffect(() => {
    if (settings) setData(settings);
  }, [settings]);

  const handleSave = () => {
    onSave(data);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bağlantı Ayarları</CardTitle>
        <CardDescription>Nebim V3 ve Shopify API bağlantı bilgilerini girin</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Nebim V3 Settings */}
        <div>
          <h3 className="form-section-header">NEBIM V3 AYARLARI</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="nebim-url">Nebim Entegratör URL</Label>
              <Input
                id="nebim-url"
                value={data.nebim_url || ""}
                onChange={(e) => setData({ ...data, nebim_url: e.target.value })}
                placeholder="https://nebim.firmaniz.com/api"
                data-testid="input-nebim-url"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nebim-username">Kullanıcı Adı</Label>
              <Input
                id="nebim-username"
                value={data.nebim_username || ""}
                onChange={(e) => setData({ ...data, nebim_username: e.target.value })}
                placeholder="Nebim kullanıcı adı"
                data-testid="input-nebim-username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nebim-password">Şifre</Label>
              <Input
                id="nebim-password"
                type="password"
                value={data.nebim_password || ""}
                onChange={(e) => setData({ ...data, nebim_password: e.target.value })}
                placeholder="••••••••"
                data-testid="input-nebim-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="barcode-type">Barkod Tipi</Label>
              <Input
                id="barcode-type"
                value={data.barcode_type || ""}
                onChange={(e) => setData({ ...data, barcode_type: e.target.value })}
                placeholder="Örn: EAN13, AEAN13"
                data-testid="input-barcode-type"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lang-code">Dil Kodu</Label>
              <Select value={data.lang_code || "TR"} onValueChange={(v) => setData({ ...data, lang_code: v })}>
                <SelectTrigger data-testid="select-lang-code">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TR">Türkçe (TR)</SelectItem>
                  <SelectItem value="EN">English (EN)</SelectItem>
                  <SelectItem value="DE">Deutsch (DE)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Shopify Settings */}
        <div>
          <h3 className="form-section-header">SHOPIFY AYARLARI</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="shopify-url">Mağaza URL</Label>
              <Input
                id="shopify-url"
                value={data.shopify_store_url || ""}
                onChange={(e) => setData({ ...data, shopify_store_url: e.target.value })}
                placeholder="magazaniz.myshopify.com"
                data-testid="input-shopify-url"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="shopify-api-key">API Key</Label>
              <Input
                id="shopify-api-key"
                value={data.shopify_api_key || ""}
                onChange={(e) => setData({ ...data, shopify_api_key: e.target.value })}
                placeholder="Shopify API Key"
                data-testid="input-shopify-api-key"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="shopify-api-secret">API Secret</Label>
              <Input
                id="shopify-api-secret"
                type="password"
                value={data.shopify_api_secret || ""}
                onChange={(e) => setData({ ...data, shopify_api_secret: e.target.value })}
                placeholder="••••••••"
                data-testid="input-shopify-api-secret"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="shopify-access-token">Access Token</Label>
              <Input
                id="shopify-access-token"
                type="password"
                value={data.shopify_access_token || ""}
                onChange={(e) => setData({ ...data, shopify_access_token: e.target.value })}
                placeholder="••••••••"
                data-testid="input-shopify-access-token"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={handleSave} disabled={saving} data-testid="btn-save-connection">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Product Mapping Settings
function ProductMappingSettings({ settings, onSave, saving }) {
  const [data, setData] = useState(settings || {});

  useEffect(() => {
    if (settings) setData(settings);
  }, [settings]);

  const nebimFields = [
    "ProductName", "ProductDescription", "ProductCode", "Barcode", "BrandCode", 
    "CategoryCode", "Weight", "Color", "Size", "Material"
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ürün Eşleştirme Ayarları</CardTitle>
        <CardDescription>Shopify alanlarını Nebim alanlarıyla eşleştirin</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Ürün Başlığı</Label>
            <Select value={data.title_field || "ProductName"} onValueChange={(v) => setData({ ...data, title_field: v })}>
              <SelectTrigger data-testid="select-title-field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {nebimFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Ürün Açıklaması</Label>
            <Select value={data.description_field || "ProductDescription"} onValueChange={(v) => setData({ ...data, description_field: v })}>
              <SelectTrigger data-testid="select-description-field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {nebimFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>SKU</Label>
            <Select value={data.sku_field || "ProductCode"} onValueChange={(v) => setData({ ...data, sku_field: v })}>
              <SelectTrigger data-testid="select-sku-field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {nebimFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Barkod</Label>
            <Select value={data.barcode_field || "Barcode"} onValueChange={(v) => setData({ ...data, barcode_field: v })}>
              <SelectTrigger data-testid="select-barcode-field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {nebimFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Marka</Label>
            <Select value={data.brand_field || "BrandCode"} onValueChange={(v) => setData({ ...data, brand_field: v })}>
              <SelectTrigger data-testid="select-brand-field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {nebimFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Kategori</Label>
            <Select value={data.category_field || "CategoryCode"} onValueChange={(v) => setData({ ...data, category_field: v })}>
              <SelectTrigger data-testid="select-category-field">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {nebimFields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={() => onSave(data)} disabled={saving} data-testid="btn-save-product-mapping">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Order Settings Component
function OrderSettings({ settings, onSave, saving }) {
  const [data, setData] = useState(settings || {});

  useEffect(() => {
    if (settings) setData(settings);
  }, [settings]);

  const productTypes = ["Ürün", "Hizmet", "Masraf", "Malzeme"];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sipariş Ayarları</CardTitle>
        <CardDescription>Sipariş aktarım ayarlarını yapılandırın</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[600px] pr-4">
          <div className="space-y-6">
            {/* Basic Settings */}
            <div>
              <h3 className="form-section-header">TEMEL AYARLAR</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>POS Terminal Kodu</Label>
                  <Input
                    value={data.pos_terminal || data.pos_terminal_code || ""}
                    onChange={(e) => setData({ ...data, pos_terminal_code: e.target.value, pos_terminal: e.target.value })}
                    data-testid="input-pos-terminal"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Depo Kodu</Label>
                  <Input
                    value={data.warehouse_code || ""}
                    onChange={(e) => setData({ ...data, warehouse_code: e.target.value })}
                    data-testid="input-warehouse-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ofis Kodu</Label>
                  <Input
                    value={data.office_code || ""}
                    onChange={(e) => setData({ ...data, office_code: e.target.value })}
                    data-testid="input-office-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mağaza Kodu</Label>
                  <Input
                    value={data.store_code || ""}
                    onChange={(e) => setData({ ...data, store_code: e.target.value })}
                    data-testid="input-store-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sipariş Modu</Label>
                  <Select value={data.order_mode || "Perakende Sipariş"} onValueChange={(v) => setData({ ...data, order_mode: v })}>
                    <SelectTrigger data-testid="select-order-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Perakende Sipariş">Perakende Sipariş</SelectItem>
                      <SelectItem value="Toptan Sipariş">Toptan Sipariş</SelectItem>
                      <SelectItem value="Perakende Satış Fatura">Perakende Satış Fatura</SelectItem>
                      <SelectItem value="Toptan Satış Fatura">Toptan Satış Fatura</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>İndirim Tipi Kodu</Label>
                  <Input
                    value={data.discount_type || data.discount_type_code || ""}
                    onChange={(e) => setData({ ...data, discount_type_code: e.target.value, discount_type: e.target.value })}
                    data-testid="input-discount-type"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Belge No Ön Ek</Label>
                  <Input
                    value={data.document_prefix || ""}
                    onChange={(e) => setData({ ...data, document_prefix: e.target.value })}
                    data-testid="input-document-prefix"
                  />
                </div>
              </div>
            </div>

            {/* Product Codes */}
            <div>
              <h3 className="form-section-header">ÜRÜN KODLARI</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Kargo Ürün Kodu</Label>
                  <Input
                    value={data.shipping_product_code || ""}
                    onChange={(e) => setData({ ...data, shipping_product_code: e.target.value })}
                    data-testid="input-shipping-product"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kargo Ürün Tipi</Label>
                  <Select value={data.shipping_product_type || "Hizmet"} onValueChange={(v) => setData({ ...data, shipping_product_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {productTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Kapıda Ödeme Ürün Kodu</Label>
                  <Input
                    value={data.cod_product_code || ""}
                    onChange={(e) => setData({ ...data, cod_product_code: e.target.value })}
                    data-testid="input-cod-product"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kapıda Ödeme Tipi</Label>
                  <Select value={data.cod_product_type || "Hizmet"} onValueChange={(v) => setData({ ...data, cod_product_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {productTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Banka Komisyon Ürün Kodu</Label>
                  <Input
                    value={data.bank_commission_code || ""}
                    onChange={(e) => setData({ ...data, bank_commission_code: e.target.value })}
                    data-testid="input-bank-commission"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Banka Komisyon Tipi</Label>
                  <Select value={data.bank_commission_type || "Masraf"} onValueChange={(v) => setData({ ...data, bank_commission_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {productTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Hediye Paketi Ürün Kodu</Label>
                  <Input
                    value={data.gift_package_code || ""}
                    onChange={(e) => setData({ ...data, gift_package_code: e.target.value })}
                    data-testid="input-gift-package"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hediye Paketi Tipi</Label>
                  <Select value={data.gift_package_type || "Hizmet"} onValueChange={(v) => setData({ ...data, gift_package_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {productTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Checkboxes */}
            <div>
              <h3 className="form-section-header">AKTARIM SEÇENEKLERİ</h3>
              <div className="checkbox-grid">
                {[
                  { key: "transfer_as_credit", label: "Vadeli Olarak Aktar" },
                  { key: "einvoice_check", label: "E-Fatura Mükellef Kontrolü" },
                  { key: "update_invoice_url", label: "Fatura URL Güncellensin" },
                  { key: "transfer_by_customer_code", label: "Müşteri Koduna Göre Aktar" },
                  { key: "notify_gift_voucher", label: "Nebim'e Hediye Çeki Bildir" },
                  { key: "create_reservation", label: "Rezerve Oluştur" },
                  { key: "transfer_product_discount", label: "Sipariş Aktarımında Ürün İndirimi" },
                  { key: "transfer_as_try", label: "Siparişleri TRY Olarak Aktar" },
                  { key: "transfer_by_barcode", label: "Siparişi Barkoda Göre Aktar" },
                  { key: "assorted_order_transfer", label: "Asortili Sipariş Aktarımı" },
                ].map((item) => (
                  <div key={item.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={item.key}
                      checked={data[item.key] || false}
                      onCheckedChange={(checked) => setData({ ...data, [item.key]: checked })}
                      data-testid={`checkbox-${item.key}`}
                    />
                    <Label htmlFor={item.key} className="text-sm cursor-pointer">{item.label}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="flex justify-end pt-4 border-t mt-6">
          <Button onClick={() => onSave(data)} disabled={saving} data-testid="btn-save-order">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Update Settings Component (with many checkboxes)
function UpdateSettings({ settings, onSave, saving }) {
  const [data, setData] = useState(settings || {});

  useEffect(() => {
    if (settings) setData(settings);
  }, [settings]);

  const productUpdateFields = [
    { key: "product_name", label: "Ürün Adı" },
    { key: "stock_code", label: "Stok Kodu" },
    { key: "brand", label: "Marka" },
    { key: "description", label: "Açıklama" },
    { key: "supplier", label: "Tedarikçi" },
    { key: "technical_detail", label: "Teknik Detay" },
    { key: "barcode", label: "Barkod" },
    { key: "category", label: "Kategori" },
    { key: "previous_categories_delete", label: "Önceki Kategoriler Silinsin" },
    { key: "extra_option", label: "Ek Seçenek" },
    { key: "prefix_text", label: "Ön Yazı" },
    { key: "tag", label: "Etiket" },
    { key: "product_depth", label: "Ürün Derinliği" },
    { key: "adwords_description", label: "Adwords Açıklama" },
    { key: "seo_description", label: "Seo Açıklama" },
    { key: "seo_keyword", label: "Seo Keyword" },
    { key: "seo_title", label: "Seo Başlık" },
    { key: "seo_no_index", label: "Seo No Index" },
    { key: "seo_no_follow", label: "Seo No Follow" },
    { key: "assort_group", label: "Asorti Grup" },
    { key: "min_quantity", label: "Ürün Adedi Minimum Değer" },
    { key: "default_quantity", label: "Ürün Adedi Varsayılan Değer" },
    { key: "product_weight", label: "Ürün Ağırlığı" },
    { key: "max_member_purchase", label: "Üye Alım Maksimum" },
    { key: "min_member_purchase", label: "Üye Alım Minimum" },
    { key: "gtip_code", label: "GTIP Kodu" },
    { key: "product_width", label: "Ürün Genişliği" },
    { key: "adwords_product_type", label: "Adwords Ürün Tipi" },
    { key: "max_installment", label: "Maksimum Taksit Sayısı" },
    { key: "estimated_delivery", label: "Tahmini Teslim Süresi" },
    { key: "custom_field_1", label: "Özel Alan 1" },
    { key: "custom_field_2", label: "Özel Alan 2" },
    { key: "custom_field_3", label: "Özel Alan 3" },
    { key: "custom_field_4", label: "Özel Alan 4" },
    { key: "custom_field_5", label: "Özel Alan 5" },
    { key: "marketplace_group_1", label: "Marketplace Grup 1" },
    { key: "marketplace_group_2", label: "Marketplace Grup 2" },
    { key: "marketplace_group_3", label: "Marketplace Grup 3" },
    { key: "marketplace_group_4", label: "Marketplace Grup 4" },
    { key: "marketplace_group_5", label: "Marketplace Grup 5" },
    { key: "product_height", label: "Ürün Yüksekliği" },
    { key: "sales_unit", label: "Satış Birimi" },
    { key: "active_product_card", label: "Aktif (Ürün Kartı)" },
    { key: "active_variation", label: "Aktif (Varyasyon)" },
    { key: "base64_image", label: "Base64 Resim" },
    { key: "search_keyword", label: "Arama Kelime" },
    { key: "desi", label: "Desi" },
    { key: "desi_2", label: "Desi 2" },
    { key: "stock_limit", label: "Stok Limiti" },
    { key: "delete_previous_images_card", label: "Önceki Resimleri Sil (Ürün Kartı)" },
    { key: "add_images_non_official", label: "Resmi Olmayanlara Resim Ekle" },
    { key: "quantity_increment", label: "Ürün Adedi Artış Kademesi" },
    { key: "add_images_variation", label: "Resmi Olmayanlara Resim Ekle (Varyasyon)" },
    { key: "delete_previous_images_variation", label: "Önceki Resimleri Sil (Varyasyon)" },
    { key: "adwords_category", label: "Adwords Ürün Kategori" },
    { key: "origin_country", label: "Menşei Ülke" },
  ];

  const stockPriceFields = [
    { key: "purchase_price", label: "Alış Fiyatı" },
    { key: "sale_price", label: "Satış Fiyatı" },
    { key: "discounted_price", label: "İndirimli Fiyat" },
    { key: "stock_quantity", label: "Stok Adedi" },
    { key: "vat_rate", label: "Kdv Oran" },
    { key: "vat_included", label: "Kdv Dahil" },
    { key: "country_prices", label: "Ülke Fiyatları" },
    { key: "currency", label: "Para Birimi" },
    { key: "member_price_1", label: "Üye Tipi Fiyat 1" },
    { key: "member_price_2", label: "Üye Tipi Fiyat 2" },
    { key: "member_price_3", label: "Üye Tipi Fiyat 3" },
    { key: "member_price_4", label: "Üye Tipi Fiyat 4" },
    { key: "member_price_5", label: "Üye Tipi Fiyat 5" },
    { key: "member_price_6", label: "Üye Tipi Fiyat 6" },
    { key: "member_price_7", label: "Üye Tipi Fiyat 7" },
    { key: "member_price_8", label: "Üye Tipi Fiyat 8" },
    { key: "member_price_9", label: "Üye Tipi Fiyat 9" },
    { key: "member_price_10", label: "Üye Tipi Fiyat 10" },
    { key: "member_price_11", label: "Üye Tipi Fiyat 11" },
    { key: "member_price_12", label: "Üye Tipi Fiyat 12" },
    { key: "member_price_13", label: "Üye Tipi Fiyat 13" },
    { key: "member_price_14", label: "Üye Tipi Fiyat 14" },
    { key: "member_price_15", label: "Üye Tipi Fiyat 15" },
    { key: "member_price_16", label: "Üye Tipi Fiyat 16" },
    { key: "member_price_17", label: "Üye Tipi Fiyat 17" },
    { key: "member_price_18", label: "Üye Tipi Fiyat 18" },
    { key: "member_price_19", label: "Üye Tipi Fiyat 19" },
    { key: "member_price_20", label: "Üye Tipi Fiyat 20" },
    { key: "market_price", label: "Piyasa Fiyatı" },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Güncelleme Ayarları</CardTitle>
        <CardDescription>Hangi alanların güncelleneceğini seçin</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[600px] pr-4">
          <div className="space-y-8">
            {/* Product Update Settings */}
            <div>
              <h3 className="form-section-header">ÜRÜN GÜNCELLEME AYARLARI</h3>
              <div className="checkbox-grid">
                {productUpdateFields.map((field) => (
                  <div key={field.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={field.key}
                      checked={data[field.key] || false}
                      onCheckedChange={(checked) => setData({ ...data, [field.key]: checked })}
                      data-testid={`checkbox-update-${field.key}`}
                    />
                    <Label htmlFor={field.key} className="text-sm cursor-pointer">{field.label}</Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Stock Price Update Settings */}
            <div>
              <h3 className="form-section-header">STOK - FİYAT GÜNCELLEME AYARLARI</h3>
              <div className="checkbox-grid">
                {stockPriceFields.map((field) => (
                  <div key={field.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={field.key}
                      checked={data[field.key] || false}
                      onCheckedChange={(checked) => setData({ ...data, [field.key]: checked })}
                      data-testid={`checkbox-stock-${field.key}`}
                    />
                    <Label htmlFor={field.key} className="text-sm cursor-pointer">{field.label}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="flex justify-end pt-4 border-t mt-6">
          <Button onClick={() => onSave(data)} disabled={saving} data-testid="btn-save-update">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Auto Transfer Settings
function AutoTransferSettings({ settings, onSave, saving }) {
  const [data, setData] = useState(settings || {});

  useEffect(() => {
    if (settings) setData(settings);
  }, [settings]);

  const orderStatuses = [
    { key: "order_received", label: "Siparişiniz Alındı" },
    { key: "awaiting_approval", label: "Onay Bekliyor" },
    { key: "approved", label: "Onaylandı" },
    { key: "awaiting_payment", label: "Ödeme Bekliyor" },
    { key: "being_packaged", label: "Paketleniyor" },
    { key: "being_supplied", label: "Tedarik Ediliyor" },
    { key: "shipped", label: "Kargoya Verildi" },
    { key: "delivered", label: "Teslim Edildi" },
    { key: "cancelled", label: "İptal" },
    { key: "returned", label: "İade" },
    { key: "deleted", label: "Silinmiş" },
    { key: "return_request", label: "İade Talep Alındı" },
    { key: "not_delivered", label: "Teslim Edilemedi" },
    { key: "partial_return_request", label: "Kısmi İade Talebi" },
    { key: "partial_return_done", label: "Kısmi İade Yapıldı" },
  ];

  const packageStatuses = [
    { key: "pending", label: "Beklemede" },
    { key: "packaging", label: "Paketleniyor" },
    { key: "missing_item", label: "Eksik Ürün" },
    { key: "invoice_issued", label: "Fatura Kesildi" },
    { key: "incomplete_shipped", label: "Eksik Gönderildi" },
    { key: "invoice_pending", label: "Fatura Bekliyor" },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Otomatik Aktarım Ayarları</CardTitle>
        <CardDescription>Otomatik senkronizasyon ayarlarını yapılandırın</CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[600px] pr-4">
          <div className="space-y-8">
            {/* Main Toggles */}
            <div>
              <h3 className="form-section-header">OTOMATİK AKTARIM AYARLARI</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium">Sipariş Aktarımı</Label>
                    <p className="text-xs text-slate-500">Siparişleri otomatik aktar</p>
                  </div>
                  <Switch
                    checked={data.order_transfer_active || false}
                    onCheckedChange={(checked) => setData({ ...data, order_transfer_active: checked })}
                    data-testid="switch-order-transfer"
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium">ERP Olmayanları Stok 0 Yap</Label>
                    <p className="text-xs text-slate-500">ERP'de bulunmayan ürünlerin stokunu sıfırla</p>
                  </div>
                  <Switch
                    checked={data.erp_missing_stock_zero || false}
                    onCheckedChange={(checked) => setData({ ...data, erp_missing_stock_zero: checked })}
                    data-testid="switch-erp-stock-zero"
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium">Yeni Ürün Aktarılsın</Label>
                    <p className="text-xs text-slate-500">Yeni ürünleri otomatik aktar</p>
                  </div>
                  <Switch
                    checked={data.new_product_transfer || false}
                    onCheckedChange={(checked) => setData({ ...data, new_product_transfer: checked })}
                    data-testid="switch-new-product"
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium">Stok Fiyat Aktarımı</Label>
                    <p className="text-xs text-slate-500">Stok ve fiyat bilgilerini otomatik aktar</p>
                  </div>
                  <Switch
                    checked={data.stock_price_transfer_active || false}
                    onCheckedChange={(checked) => setData({ ...data, stock_price_transfer_active: checked })}
                    data-testid="switch-stock-price"
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium">İptal / İade Aktarımı</Label>
                    <p className="text-xs text-slate-500">İptal ve iade işlemlerini otomatik aktar</p>
                  </div>
                  <Switch
                    checked={data.cancel_return_transfer_active || false}
                    onCheckedChange={(checked) => setData({ ...data, cancel_return_transfer_active: checked })}
                    data-testid="switch-cancel-return"
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <Label className="font-medium">Mağaza Stokları Aktarımı</Label>
                    <p className="text-xs text-slate-500">Mağaza stoklarını otomatik aktar</p>
                  </div>
                  <Switch
                    checked={data.store_stock_transfer_active || false}
                    onCheckedChange={(checked) => setData({ ...data, store_stock_transfer_active: checked })}
                    data-testid="switch-store-stock"
                  />
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Label className="font-medium">Ürün ve Sipariş Aktarım Süresi (Dakika)</Label>
                  <Input
                    type="number"
                    className="mt-2 w-32"
                    value={data.transfer_interval_minutes || 4}
                    onChange={(e) => setData({ ...data, transfer_interval_minutes: parseInt(e.target.value) || 4 })}
                    data-testid="input-transfer-interval"
                  />
                </div>
              </div>
            </div>

            {/* Order Statuses */}
            <div>
              <h3 className="form-section-header">AKTARILACAK SİPARİŞ DURUMLARI</h3>
              <div className="checkbox-grid">
                {orderStatuses.map((status) => (
                  <div key={status.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={status.key}
                      checked={data[status.key] || false}
                      onCheckedChange={(checked) => setData({ ...data, [status.key]: checked })}
                      data-testid={`checkbox-status-${status.key}`}
                    />
                    <Label htmlFor={status.key} className="text-sm cursor-pointer">{status.label}</Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Package Statuses */}
            <div>
              <h3 className="form-section-header">AKTARILACAK PAKETLEME DURUMLARI</h3>
              <div className="checkbox-grid">
                {packageStatuses.map((status) => (
                  <div key={status.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={status.key}
                      checked={data[status.key] || false}
                      onCheckedChange={(checked) => setData({ ...data, [status.key]: checked })}
                      data-testid={`checkbox-package-${status.key}`}
                    />
                    <Label htmlFor={status.key} className="text-sm cursor-pointer">{status.label}</Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Transfer Settings */}
            <div>
              <h3 className="form-section-header">AKTARIM AYARLARI</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="colors_separate"
                    checked={data.colors_separate_product || false}
                    onCheckedChange={(checked) => setData({ ...data, colors_separate_product: checked })}
                    data-testid="checkbox-colors-separate"
                  />
                  <Label htmlFor="colors_separate" className="cursor-pointer">Renkler Ayrı Ürün Aktarılsın</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="assign_related"
                    checked={data.assign_related_same || false}
                    onCheckedChange={(checked) => setData({ ...data, assign_related_same: checked })}
                    data-testid="checkbox-assign-related"
                  />
                  <Label htmlFor="assign_related" className="cursor-pointer">İlgili Ürün Aynı Ürün Olarak Atansın</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="send_mail"
                    checked={data.send_related_mail || false}
                    onCheckedChange={(checked) => setData({ ...data, send_related_mail: checked })}
                    data-testid="checkbox-send-mail"
                  />
                  <Label htmlFor="send_mail" className="cursor-pointer">İlgili Ürün Mail Gönderme</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="lower_price"
                    checked={data.main_product_lower_price || false}
                    onCheckedChange={(checked) => setData({ ...data, main_product_lower_price: checked })}
                    data-testid="checkbox-lower-price"
                  />
                  <Label htmlFor="lower_price" className="cursor-pointer">Ürünlerde Ana Ürün Düşük Fiyatlı Olsun</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="category_code"
                    checked={data.match_by_category_code || false}
                    onCheckedChange={(checked) => setData({ ...data, match_by_category_code: checked })}
                    data-testid="checkbox-category-code"
                  />
                  <Label htmlFor="category_code" className="cursor-pointer">Kategori Koduna Göre Eşleştir</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="new_passive"
                    checked={data.new_product_passive || false}
                    onCheckedChange={(checked) => setData({ ...data, new_product_passive: checked })}
                    data-testid="checkbox-new-passive"
                  />
                  <Label htmlFor="new_passive" className="cursor-pointer">Yeni Ürün Pasif Aktarılsın</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="price_zero"
                    checked={data.price_zero_stock_zero || false}
                    onCheckedChange={(checked) => setData({ ...data, price_zero_stock_zero: checked })}
                    data-testid="checkbox-price-zero"
                  />
                  <Label htmlFor="price_zero" className="cursor-pointer">Fiyat 0 Gelen Ürünler Stok 0 Aktarılsın</Label>
                </div>
                <div className="space-y-2">
                  <Label>İlgili Ürün Aktarım Baz Alınacak Yer</Label>
                  <Select value={data.related_product_base || "StockCode"} onValueChange={(v) => setData({ ...data, related_product_base: v })}>
                    <SelectTrigger className="w-48" data-testid="select-related-base">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="StockCode">Stok Kodu</SelectItem>
                      <SelectItem value="Barcode">Barkod</SelectItem>
                      <SelectItem value="ProductCode">Ürün Kodu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        <div className="flex justify-end pt-4 border-t mt-6">
          <Button onClick={() => onSave(data)} disabled={saving} data-testid="btn-save-auto-transfer">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Payment Codes Settings
function PaymentCodesSettings({ token }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState({
    shopify_payment_type: "",
    shopify_shipping_company: "",
    nebim_account_type: "",
    nebim_payment_code: "",
  });

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const response = await axios.get(`${API}/settings/payment-codes?token=${token}`);
      setCodes(response.data);
    } catch (error) {
      toast.error("Ödeme kodları yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const addCode = async () => {
    if (!newCode.shopify_payment_type || !newCode.nebim_payment_code) {
      toast.error("Zorunlu alanları doldurun");
      return;
    }
    try {
      await axios.post(`${API}/settings/payment-codes?token=${token}`, newCode);
      toast.success("Ödeme kodu eklendi");
      setNewCode({ shopify_payment_type: "", shopify_shipping_company: "", nebim_account_type: "", nebim_payment_code: "" });
      fetchCodes();
    } catch (error) {
      toast.error("Ödeme kodu eklenemedi");
    }
  };

  const deleteCode = async (id) => {
    try {
      await axios.delete(`${API}/settings/payment-codes/${id}?token=${token}`);
      toast.success("Ödeme kodu silindi");
      fetchCodes();
    } catch (error) {
      toast.error("Ödeme kodu silinemedi");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ödeme Kodu Tanımlama</CardTitle>
        <CardDescription>Shopify ödeme tiplerini Nebim kodlarıyla eşleştirin</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add New */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-slate-50 rounded-lg">
          <div className="space-y-2">
            <Label>Shopify Ödeme Tipi</Label>
            <Input
              value={newCode.shopify_payment_type}
              onChange={(e) => setNewCode({ ...newCode, shopify_payment_type: e.target.value })}
              placeholder="Kredi Kartı"
              data-testid="input-new-payment-type"
            />
          </div>
          <div className="space-y-2">
            <Label>Shopify Kargo Firma</Label>
            <Input
              value={newCode.shopify_shipping_company}
              onChange={(e) => setNewCode({ ...newCode, shopify_shipping_company: e.target.value })}
              placeholder="Aras Kargo"
              data-testid="input-new-shipping"
            />
          </div>
          <div className="space-y-2">
            <Label>Nebim Hesap Türü</Label>
            <Input
              value={newCode.nebim_account_type}
              onChange={(e) => setNewCode({ ...newCode, nebim_account_type: e.target.value })}
              placeholder="NAKIT"
              data-testid="input-new-account-type"
            />
          </div>
          <div className="space-y-2">
            <Label>Nebim Ödeme Kodu</Label>
            <Input
              value={newCode.nebim_payment_code}
              onChange={(e) => setNewCode({ ...newCode, nebim_payment_code: e.target.value })}
              placeholder="KK01"
              data-testid="input-new-payment-code"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={addCode} data-testid="btn-add-payment-code">
              <Plus className="w-4 h-4 mr-2" />
              Ekle
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Shopify Ödeme Tipi</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Shopify Kargo</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Nebim Hesap Türü</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Nebim Ödeme Kodu</th>
                <th className="text-right p-3 text-sm font-medium text-slate-600">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((code) => (
                <tr key={code.id} className="border-t">
                  <td className="p-3 text-sm">{code.shopify_payment_type}</td>
                  <td className="p-3 text-sm">{code.shopify_shipping_company || "-"}</td>
                  <td className="p-3 text-sm">{code.nebim_account_type || "-"}</td>
                  <td className="p-3 text-sm">{code.nebim_payment_code}</td>
                  <td className="p-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => deleteCode(code.id)} data-testid={`btn-delete-payment-${code.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
              {codes.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-slate-500">
                    Henüz ödeme kodu tanımlanmamış
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// E-Archive Settings
function EArchiveSettings({ token }) {
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDef, setNewDef] = useState({
    shopify_payment_type: "",
    shopify_order_source: "",
    nebim_payment_description: "",
    nebim_payment_agent: "",
    nebim_sales_web_address: "",
  });

  useEffect(() => {
    fetchDefinitions();
  }, []);

  const fetchDefinitions = async () => {
    try {
      const response = await axios.get(`${API}/settings/earchive-definitions?token=${token}`);
      setDefinitions(response.data);
    } catch (error) {
      toast.error("E-Arşiv tanımları yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const addDefinition = async () => {
    if (!newDef.shopify_payment_type) {
      toast.error("Shopify ödeme tipi zorunludur");
      return;
    }
    try {
      await axios.post(`${API}/settings/earchive-definitions?token=${token}`, newDef);
      toast.success("E-Arşiv tanımı eklendi");
      setNewDef({ shopify_payment_type: "", shopify_order_source: "", nebim_payment_description: "", nebim_payment_agent: "", nebim_sales_web_address: "" });
      fetchDefinitions();
    } catch (error) {
      toast.error("E-Arşiv tanımı eklenemedi");
    }
  };

  const deleteDefinition = async (id) => {
    try {
      await axios.delete(`${API}/settings/earchive-definitions/${id}?token=${token}`);
      toast.success("E-Arşiv tanımı silindi");
      fetchDefinitions();
    } catch (error) {
      toast.error("E-Arşiv tanımı silinemedi");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>E-Arşiv Tanımlamaları</CardTitle>
        <CardDescription>E-Arşiv fatura ayarlarını yapılandırın</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Add New */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
          <div className="space-y-2">
            <Label>Shopify Ödeme Tipi</Label>
            <Input
              value={newDef.shopify_payment_type}
              onChange={(e) => setNewDef({ ...newDef, shopify_payment_type: e.target.value })}
              placeholder="Kredi Kartı"
              data-testid="input-earchive-payment"
            />
          </div>
          <div className="space-y-2">
            <Label>Shopify Sipariş Kaynağı</Label>
            <Input
              value={newDef.shopify_order_source}
              onChange={(e) => setNewDef({ ...newDef, shopify_order_source: e.target.value })}
              placeholder="Web"
              data-testid="input-earchive-source"
            />
          </div>
          <div className="space-y-2">
            <Label>Nebim Ödeme Tipi Açıklama</Label>
            <Input
              value={newDef.nebim_payment_description}
              onChange={(e) => setNewDef({ ...newDef, nebim_payment_description: e.target.value })}
              placeholder="Kredi Kartı ile Ödeme"
              data-testid="input-earchive-description"
            />
          </div>
          <div className="space-y-2">
            <Label>Nebim Ödeme Aracısı</Label>
            <Input
              value={newDef.nebim_payment_agent}
              onChange={(e) => setNewDef({ ...newDef, nebim_payment_agent: e.target.value })}
              placeholder="IYZICO"
              data-testid="input-earchive-agent"
            />
          </div>
          <div className="space-y-2">
            <Label>Nebim Satış Web Adresi</Label>
            <Input
              value={newDef.nebim_sales_web_address}
              onChange={(e) => setNewDef({ ...newDef, nebim_sales_web_address: e.target.value })}
              placeholder="www.magazaniz.com"
              data-testid="input-earchive-web"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={addDefinition} data-testid="btn-add-earchive">
              <Plus className="w-4 h-4 mr-2" />
              Ekle
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Shopify Ödeme</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Sipariş Kaynağı</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Nebim Açıklama</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Ödeme Aracısı</th>
                <th className="text-left p-3 text-sm font-medium text-slate-600">Web Adresi</th>
                <th className="text-right p-3 text-sm font-medium text-slate-600">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {definitions.map((def) => (
                <tr key={def.id} className="border-t">
                  <td className="p-3 text-sm">{def.shopify_payment_type}</td>
                  <td className="p-3 text-sm">{def.shopify_order_source || "-"}</td>
                  <td className="p-3 text-sm">{def.nebim_payment_description || "-"}</td>
                  <td className="p-3 text-sm">{def.nebim_payment_agent || "-"}</td>
                  <td className="p-3 text-sm">{def.nebim_sales_web_address || "-"}</td>
                  <td className="p-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => deleteDefinition(def.id)} data-testid={`btn-delete-earchive-${def.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
              {definitions.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500">
                    Henüz E-Arşiv tanımı yapılmamış
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}


// ==================== Bundle Transfer Settings ====================
function BundleTransferSettings({ settings, token, onSave, saving }) {
  const defaults = {
    update_title: true,
    update_description: true,
    update_photos: false,
    update_price: true,
    update_stock: true,
    update_metafields: true,
    update_translations: true,
  };
  const [form, setForm] = useState({ ...defaults, ...settings });

  useEffect(() => {
    if (settings) setForm(prev => ({ ...defaults, ...settings }));
  }, [settings]);

  const toggleField = (field) => {
    setForm(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const fields = [
    { key: "update_title", label: "Ürün Başlığı", desc: "Shopify'daki ürün adını günceller" },
    { key: "update_description", label: "Açıklama & Note", desc: "Ürün açıklaması, beden tablosu ve editör notunu günceller" },
    { key: "update_photos", label: "Fotoğraflar", desc: "Nebim'den fotoğrafları tekrar çekip Shopify'a yükler (ağır işlem)", warn: true },
    { key: "update_price", label: "Fiyat", desc: "Lot fiyatını günceller" },
    { key: "update_stock", label: "Stok", desc: "Lot stok miktarını günceller" },
    { key: "update_metafields", label: "Birim Fiyat (Metafield)", desc: "custom.birim_fiyat metafield değerini günceller" },
    { key: "update_translations", label: "Çoklu Dil Çevirisi", desc: "Ek dillerdeki çevirileri günceller" },
  ];

  return (
    <Card data-testid="bundle-transfer-settings">
      <CardHeader>
        <CardTitle>Asorti Aktarım Ayarları</CardTitle>
        <CardDescription>
          Mevcut bir ürünü tekrar gönderdiğinizde hangi alanların güncelleneceğini seçin.
          Yeni ürünlerde tüm alanlar her zaman gönderilir.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-1">
        {fields.map(f => (
          <div key={f.key} className={`flex items-center justify-between p-3 rounded-lg border ${f.warn && form[f.key] ? 'border-amber-300 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`} data-testid={`bt-${f.key}`}>
            <div className="space-y-0.5">
              <div className="font-medium text-sm text-slate-900">{f.label}</div>
              <div className="text-xs text-slate-500">{f.desc}</div>
              {f.warn && form[f.key] && (
                <div className="text-xs text-amber-600 font-medium">Dikkat: Bu alan aktarımı yavaşlatabilir</div>
              )}
            </div>
            <Switch
              checked={form[f.key]}
              onCheckedChange={() => toggleField(f.key)}
              data-testid={`bt-switch-${f.key}`}
            />
          </div>
        ))}

        <div className="pt-4">
          <Button onClick={() => onSave(form)} disabled={saving} data-testid="bt-save-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
